import React, { useState, useCallback, memo, useEffect } from "react"
import { Card, List, Badge, Empty } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faPlay,
  faSpinner,
  faClipboardList,
  faTasks,
  faCode,
} from "@fortawesome/free-solid-svg-icons"
import RunAutomationModal from "./RunAutomationModal.component"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import JobConsoleModal from "./JobConsoleModal.component"
import OngoingJobItem from "./OngoingJobItem.component"
import CompletedJobItem from "./CompletedJobItem.component"
import EmptyState from "shared/ui/EmptyState/EmptyState.component"
import { useJobPolling } from "features/Automation/hooks/useJobPolling"
import {
  getScriptIcon,
  getScriptType,
  isJobOngoing,
  isJobCompleted,
} from "utils/automationUtils"
import {
  ScriptExecutionParameters,
  OutputProject,
} from "types/automation.types"
import {
  useRunScriptMutation,
  useCancelJobMutation,
} from "features/Automation/api/useAutomationMutations"

interface AutomationDisplayProps {
  availableScripts: string[]
}

// Define the script item structure
interface ScriptItem {
  /** Unique identifier for the item */
  key: string
  /** Script filename */
  name: string
}

// Define the props interface for the ScriptListItem component
interface ScriptListItemProps {
  /** The script item to display */
  item: ScriptItem
  /** Whether the script is currently running */
  isRunning: boolean
  /** Callback function for run action */
  onRun: (scriptName: string) => void
}

/**
 * Individual list item component for script display.
 * Memoized to prevent unnecessary rerenders.
 */
const ScriptListItem: React.FC<ScriptListItemProps> = memo(
  ({ item, isRunning, onRun }) => {
    /**
     * Handle run button click
     */
    const handleRun = useCallback((): void => {
      onRun(item.name)
    }, [item.name, onRun])

    return (
      <List.Item
        key={item.key}
        // A row action, so outlined: a list of solid buttons would leave no
        // main action on the screen.
        actions={[
          <DataQButton
            key="run"
            type="secondary"
            size="small"
            isLoading={isRunning}
            onClick={handleRun}
          >
            <FontAwesomeIcon
              icon={isRunning ? faSpinner : faPlay}
              style={{ color: "inherit" }}
            />
            {isRunning ? "Running..." : "Run"}
          </DataQButton>,
        ]}
        className="hover:bg-gray-50"
      >
        <List.Item.Meta
          className="p-3 px-5"
          avatar={
            <FontAwesomeIcon
              icon={getScriptIcon(item.name)}
              className="text-lg"
              style={{ color: "var(--dq-brand)" }}
            />
          }
          title={<span className="font-medium text-gray-900">{item.name}</span>}
          description={
            <span className="text-sm text-gray-500">
              {getScriptType(item.name)}
            </span>
          }
        />
      </List.Item>
    )
  }
)

ScriptListItem.displayName = "ScriptListItem"

/**
 * Component to display available automation scripts and manage their execution.
 * Left panel shows available scripts with run buttons.
 * Right panel shows output and ongoing jobs.
 */
const AutomationDisplay: React.FC<AutomationDisplayProps> = (props) => {
  const { availableScripts } = props

  // ========================================================================================
  // HOOKS
  // ========================================================================================

  const runScriptMutation = useRunScriptMutation()
  const cancelJobMutation = useCancelJobMutation()
  const {
    jobs,
    isPolling,
    selectedJobId,
    jobStatusData,
    isJobStatusPolling,
    startPolling,
    handleJobSelection,
    cleanup,
  } = useJobPolling()

  // ========================================================================================
  // STATE
  // ========================================================================================

  const [runningScripts, setRunningScripts] = useState<string[]>([])
  const [isRunModalVisible, setIsRunModalVisible] = useState(false)
  const [selectedScript, setSelectedScript] = useState<string>("")

  // ========================================================================================
  // COMPUTED VALUES
  // ========================================================================================

  /**
   * Transform available scripts into ScriptItem format
   */
  const scriptItems: ScriptItem[] = availableScripts.map((script, index) => ({
    key: `script-${index}`,
    name: script,
  }))

  /**
   * Get ongoing jobs (QUEUED or RUNNING)
   */
  const ongoingJobs = jobs.filter((job) => isJobOngoing(job.status))

  /**
   * Get completed jobs (for potential display in output section)
   */
  const completedJobs = jobs.filter((job) => isJobCompleted(job.status))

  // ========================================================================================
  // EFFECTS
  // ========================================================================================

  /**
   * Start polling when component mounts
   */
  useEffect(() => {
    startPolling()
    return cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Handle polling logic based on ongoing jobs and running scripts
   */
  useEffect(() => {
    const hasActiveWork = ongoingJobs.length > 0 || runningScripts.length > 0

    if (!hasActiveWork && isPolling) {
      const timeout = setTimeout(() => {
        if (ongoingJobs.length === 0 && runningScripts.length === 0) {
          // Note: We could stop polling here, but it's handled by the hook
        }
      }, 5000)

      return () => clearTimeout(timeout)
    } else if (hasActiveWork && !isPolling) {
      startPolling()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ongoingJobs.length, runningScripts.length, isPolling])

  // ========================================================================================
  // HANDLERS
  // ========================================================================================

  /**
   * Handle script execution - opens modal first
   */
  const handleRunScript = useCallback((scriptName: string): void => {
    setSelectedScript(scriptName)
    setIsRunModalVisible(true)
  }, [])

  /**
   * Handle ongoing job click to open console
   */
  const handleJobClick = useCallback(
    (jobId: string): void => {
      handleJobSelection(jobId)
    },
    [handleJobSelection]
  )

  /**
   * Handle job cancellation
   */
  const handleJobCancel = useCallback(
    (jobId: string): void => {
      cancelJobMutation.mutate(jobId)
    },
    [cancelJobMutation]
  )

  /**
   * Actually execute the script (called from modal)
   */
  const executeScript = useCallback(
    async (
      scriptName: string,
      parameters?: ScriptExecutionParameters,
      outputProject?: OutputProject
    ): Promise<void> => {
      try {
        setRunningScripts((prev) => [...prev, scriptName])
        setIsRunModalVisible(false)
        setSelectedScript("")

        if (!isPolling) {
          startPolling()
        }

        // The mutation refreshes the jobs list via invalidation on success.
        await runScriptMutation.mutateAsync({
          scriptName,
          parameters: parameters || {},
          outputProject,
        })
        setRunningScripts((prev) =>
          prev.filter((script) => script !== scriptName)
        )
      } catch (error) {
        console.error(`Error executing script ${scriptName}:`, error)

        setRunningScripts((prev) =>
          prev.filter((script) => script !== scriptName)
        )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPolling, runScriptMutation.mutateAsync]
  )

  // ========================================================================================
  // UTILITY FUNCTIONS
  // ========================================================================================

  /**
   * Check if script is currently running (being executed via API)
   */
  const isScriptRunning = useCallback(
    (script: string): boolean => {
      return runningScripts.includes(script)
    },
    [runningScripts]
  )

  /**
   * Handle modal visibility
   */
  const handleModalVisibility = useCallback((visible: boolean): void => {
    setIsRunModalVisible(visible)
    if (!visible) {
      setSelectedScript("")
    }
  }, [])

  /**
   * Handle console modal close
   */
  const handleConsoleModalClose = useCallback(() => {
    handleJobSelection(null)
  }, [handleJobSelection])

  // ========================================================================================
  // MAIN RENDER
  // ========================================================================================

  return (
    <>
      {/* Modal for script execution confirmation */}
      <RunAutomationModal
        scriptName={selectedScript}
        isVisible={isRunModalVisible}
        cancel={() => handleModalVisibility(false)}
        onExecute={executeScript}
      />

      {/* Job Console Modal */}
      <JobConsoleModal
        isVisible={!!selectedJobId}
        jobStatusData={jobStatusData}
        isPolling={isJobStatusPolling}
        onClose={handleConsoleModalClose}
      />

      <div className="flex h-full bg-gray-50 overflow-hidden p-4 box-border">
        {/* Left Panel - Available Scripts (1/3) */}
        <div className="w-1/3 pr-2 flex flex-col min-h-0">
          <Card
            title={
              <div className="flex items-center gap-3">
                <FontAwesomeIcon
                  icon={faClipboardList}
                  style={{ color: "var(--dq-brand)" }}
                />
                <span>Available Scripts</span>
              </div>
            }
            className="flex-1 flex flex-col min-h-0"
            styles={{
              body: {
                flex: 1,
                overflow: "auto",
                padding: 0,
                minHeight: 0,
              },
            }}
          >
            <List
              dataSource={scriptItems}
              renderItem={(item) => (
                <ScriptListItem
                  key={item.key}
                  item={item}
                  isRunning={isScriptRunning(item.name)}
                  onRun={handleRunScript}
                />
              )}
              locale={{
                emptyText: (
                  <Empty
                    description="No scripts available"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          </Card>
        </div>

        {/* Right Panel - Output & Jobs (2/3) */}
        <div
          className="w-2/3 pl-2 flex flex-col min-h-0"
          style={{ gap: "1rem" }}
        >
          {/* Ongoing Jobs Section */}
          <Card
            title={
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faTasks} style={{ color: "var(--dq-brand)" }} />
                <span>Ongoing Jobs</span>
                {ongoingJobs.length > 0 && (
                  <Badge
                    count={ongoingJobs.length}
                    style={{ backgroundColor: "var(--dq-brand)" }}
                  />
                )}
                {ongoingJobs.length > 0 && (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="fa-spin text-sm"
                    style={{ color: "var(--dq-brand)" }}
                  />
                )}
              </div>
            }
            className="flex-1 flex flex-col min-h-0"
            styles={{
              body: {
                flex: 1,
                overflow: "auto",
                padding: ongoingJobs.length === 0 ? 24 : 16,
                minHeight: 0,
              },
            }}
          >
            {ongoingJobs.length === 0 ? (
              <EmptyState
                icon={faTasks}
                message="No jobs currently running"
                subtitle="Jobs will appear here when scripts are executed"
              />
            ) : (
              <List
                dataSource={ongoingJobs}
                renderItem={(job) => (
                  <OngoingJobItem
                    key={job.job_id}
                    job={job}
                    isSelected={selectedJobId === job.job_id}
                    onClick={handleJobClick}
                    cancel={() => handleJobCancel(job.job_id)}
                  />
                )}
                className="p-0"
              />
            )}
          </Card>

          {/* Recent Jobs/Output Section */}
          <Card
            title={
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faCode} style={{ color: "var(--dq-brand)" }} />
                <span>Recent Jobs</span>
                {completedJobs.length > 0 && (
                  <Badge
                    count={completedJobs.length}
                    style={{ backgroundColor: "#52c41a" }}
                  />
                )}
              </div>
            }
            className="flex-1 flex flex-col min-h-0"
            styles={{
              body: {
                flex: 1,
                overflow: "auto",
                padding: completedJobs.length === 0 ? 24 : 16,
                minHeight: 0,
              },
            }}
          >
            {completedJobs.length === 0 ? (
              <EmptyState
                icon={faClipboardList}
                message="Completed jobs will appear here..."
                subtitle="Ready to execute automation scripts"
              />
            ) : (
              <List
                dataSource={completedJobs.slice(0, 10)}
                renderItem={(job) => (
                  <CompletedJobItem key={job.job_id} job={job} />
                )}
                className="p-0"
              />
            )}
          </Card>
        </div>
      </div>
    </>
  )
}

export default AutomationDisplay
